export interface FfslcRegionDefinition {
  code: string;
  name: string;
  shortName: string;
  color: string;
  darkColor: string;
  colorName: string;
  administrativeRegions: readonly string[];
  departments: readonly string[];
}

/** Fédération FFSLC : découpage des ligues, indépendant des régions administratives. */
export const FFSLC_REGIONS: readonly FfslcRegionDefinition[] = [
  // Chaque teinte atteint au moins 4,5:1 sur son fond cible : `color`
  // sur fond clair et `darkColor` sur le fond ardoise du thème sombre.
  { code: '01', name: 'Centre / Île-de-France', shortName: 'Centre', color: '#5B21B6', darkColor: '#C4B5FD', colorName: 'Violet royal', administrativeRegions: ['Centre-Val de Loire', 'Île-de-France'], departments: ['18', '28', '36', '37', '41', '45', '75', '77', '78', '91', '92', '93', '94', '95'] },
  { code: '02', name: 'Normandie', shortName: 'Normandie', color: '#075985', darkColor: '#7DD3FC', colorName: 'Bleu Manche', administrativeRegions: ['Normandie'], departments: ['14', '27', '50', '61', '76'] },
  // L'Aube (10) et l'Oise (60) sont rattachées à la région fédérale 03.
  { code: '03', name: 'Hauts-de-France', shortName: 'Hauts-de-France', color: '#006D77', darkColor: '#5EEAD4', colorName: 'Bleu pétrole', administrativeRegions: ['Hauts-de-France'], departments: ['02', '10', '51', '59', '60', '62', '80'] },
  { code: '04', name: 'Nouvelle-Aquitaine', shortName: 'Nouvelle-Aquitaine', color: '#8A2853', darkColor: '#F9A8D4', colorName: 'Prune vignoble', administrativeRegions: ['Nouvelle-Aquitaine'], departments: ['16', '17', '19', '23', '24', '33', '40', '47', '64', '79', '86', '87'] },
  { code: '05', name: 'Ligue PACA / Sud-Corse', shortName: 'PACA', color: '#C2410C', darkColor: '#FDBA74', colorName: 'Terracotta solaire', administrativeRegions: ["Provence-Alpes-Côte d'Azur", 'Corse'], departments: ['04', '05', '06', '13', '2A', '2B', '83', '84'] },
  { code: '06', name: 'Est', shortName: 'Est', color: '#52525B', darkColor: '#CBD5E1', colorName: 'Ardoise des Vosges', administrativeRegions: ['Grand Est', 'Bourgogne-Franche-Comté'], departments: ['08', '21', '25', '39', '52', '54', '55', '57', '58', '67', '68', '70', '88', '89', '90'] },
  { code: '07', name: 'Auvergne-Rhône-Alpes', shortName: 'Auvergne-Rhône-Alpes', color: '#166534', darkColor: '#86EFAC', colorName: 'Vert alpin', administrativeRegions: ['Auvergne-Rhône-Alpes'], departments: ['01', '03', '07', '15', '26', '38', '42', '43', '63', '69', '71', '73', '74'] },
  { code: '08', name: 'Occitanie', shortName: 'Occitanie', color: '#B91C1C', darkColor: '#FCA5A5', colorName: 'Brique toulousaine', administrativeRegions: ['Occitanie'], departments: ['09', '11', '12', '30', '31', '32', '34', '46', '48', '65', '66', '81', '82'] },
  { code: '09', name: 'Bretagne / Pays de la Loire', shortName: 'Bretagne · Pays de la Loire', color: '#1E3A8A', darkColor: '#93C5FD', colorName: 'Bleu Atlantique', administrativeRegions: ['Bretagne', 'Pays de la Loire'], departments: ['22', '29', '35', '44', '49', '53', '56', '72', '85'] },
];

const DEPARTMENT_TO_FFSLC = new Map<string, FfslcRegionDefinition>(
  FFSLC_REGIONS.flatMap(region => region.departments.map(department => [department, region] as const)),
);

export function getFfslcRegionForDepartment(departmentCode?: string | null): FfslcRegionDefinition | null {
  if (!departmentCode) return null;
  return DEPARTMENT_TO_FFSLC.get(departmentCode.trim().toUpperCase()) ?? null;
}

export function getFfslcRegion(code?: string | null): FfslcRegionDefinition | null {
  if (!code) return null;
  return FFSLC_REGIONS.find(region => region.code === code || region.name === code) ?? null;
}

export function getFfslcRegionForAdministrativeRegion(name?: string | null): FfslcRegionDefinition | null {
  if (!name) return null;
  const normalized = normalizeRegionName(name);
  return FFSLC_REGIONS.find(region =>
    region.administrativeRegions.some(item => normalizeRegionName(item) === normalized),
  ) ?? null;
}

function normalizeRegionName(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}
